import React, { useState, useEffect, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  CircleMarker,
  Popup,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { displayDate } from "@/lib/evidencePresentation";
import { localityDistance } from "./LocalityPanel";

function MapControls({ position, boundary, localPoints }) {
  const map = useMap();
  const originKey = position?.join(",");
  useEffect(() => {
    if (localPoints.length) {
      const points = localPoints.map((item) => [item.latitude, item.longitude]);
      if (originKey) points.push(originKey.split(",").map(Number));
      map.fitBounds(L.latLngBounds(points), { padding: [35, 35], maxZoom: 16 });
    }
  }, [map, localPoints, originKey]);
  return (
    <div className="map-view-controls">
      <button
        type="button"
        onClick={() =>
          map.setView(position || [-27.47, 153.025], position ? 16 : 10)
        }
      >
        {position ? "Matched parcel" : "Brisbane centre"}
      </button>
      {boundary && (
        <button
          type="button"
          onClick={() => map.fitBounds(L.geoJSON(boundary).getBounds())}
        >
          Pilot area
        </button>
      )}
      {localPoints.length > 0 && (
        <button
          type="button"
          onClick={() =>
            map.fitBounds(
              L.latLngBounds([
                ...(position ? [position] : []),
                ...localPoints.map((item) => [item.latitude, item.longitude]),
              ]),
              { padding: [35, 35], maxZoom: 16 },
            )
          }
        >
          Nearby records
        </button>
      )}
    </div>
  );
}

export default function PropertyResearchMap({
  location,
  locality,
  selectedLayers,
}) {
  const localPoints = useMemo(
    () =>
      (locality?.layers || [])
        .filter(
          (layer) =>
            layer.status !== "unavailable" &&
            (selectedLayers || []).includes(layer.id),
        )
        .flatMap((layer) =>
          layer.items.map((item) => ({
            ...item,
            layer: layer.id,
            status: layer.status,
            source_url: layer.source_url,
            period: layer.observation_period,
          })),
        ),
    [locality, selectedLayers],
  );
  const valid =
    location &&
    [location.latitude, location.longitude].every(
      (v) => typeof v === "number" && Number.isFinite(v),
    ) &&
    location.latitude >= -28.5 &&
    location.latitude <= -26.5 &&
    location.longitude >= 152 &&
    location.longitude <= 154.5;
  const position = valid ? [location.latitude, location.longitude] : null;
  const [tiles, setTiles] = useState(Boolean(valid));
  const [tileError, setTileError] = useState(false);
  const [tileAttempt, setTileAttempt] = useState(0);
  const [boundary, setBoundary] = useState(null);
  const [boundaryError, setBoundaryError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch("/data/brisbane-boundary.geojson", { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((data) => {
        if (
          data.type !== "Feature" ||
          !["Polygon", "MultiPolygon"].includes(data.geometry?.type)
        )
          throw new Error();
        setBoundary(data);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setBoundaryError(true);
      });
    return () => controller.abort();
  }, []);
  return (
    <section className="property-map" aria-label="Property locality map">
      <div className="map-topline">
        <span>{valid ? "Matched council parcel" : "Brisbane pilot area"}</span>
        <span>
          {location?.status === "stale"
            ? "Older location record"
            : "Context map"}
        </span>
      </div>
      <MapContainer
        key={valid ? position.join(",") : "brisbane"}
        center={position || [-27.47, 153.025]}
        zoom={valid ? 16 : 10}
        scrollWheelZoom={false}
        className="property-map-canvas"
      >
        {tiles && (
          <TileLayer
            key={tileAttempt}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            eventHandlers={{ tileerror: () => setTileError(true) }}
          />
        )}
        {boundary && (
          <GeoJSON
            data={boundary}
            style={{
              color: "#53765e",
              weight: 2,
              fillOpacity: 0.07,
              dashArray: "6 5",
            }}
          />
        )}
        {position && (
          <CircleMarker
            center={position}
            radius={9}
            pathOptions={{
              color: "#fff",
              weight: 3,
              fillColor: "#205644",
              fillOpacity: 1,
            }}
          >
            <Popup>
              Council parcel centroid. Not a building or entrance location.
              {location.status === "stale" &&
                " Older record: revalidation needed."}
            </Popup>
          </CircleMarker>
        )}
        {localPoints.map((item) => (
          <CircleMarker
            key={`${item.layer}-${item.id}`}
            center={[item.latitude, item.longitude]}
            radius={6}
            pathOptions={{
              color: "#fff",
              weight: 2,
              fillColor: item.layer === "bus_stops" ? "#98651e" : "#557752",
              fillOpacity: 1,
            }}
          >
            <Popup>
              <strong>{item.name}</strong>
              <br />
              {item.layer === "bus_stops"
                ? "Council bus-stop record"
                : "Council park point"}
              <br />
              {localityDistance(item.distance_m)}
              <br />
              Source period {displayDate(item.period)}
              {item.status === "stale" && " · Older snapshot"}
              <br />
              <a href={item.source_url}>Council source · Record {item.id}</a>
            </Popup>
          </CircleMarker>
        ))}
        <MapControls
          position={position}
          boundary={boundary}
          localPoints={localPoints}
        />
      </MapContainer>
      {!tiles && (
        <button
          className="load-map"
          type="button"
          onClick={() => setTiles(true)}
        >
          Load interactive map
        </button>
      )}
      {tileError && (
        <p role="status" className="map-message">
          Street tiles could not load. The source boundary and any matched
          centroid remain available.
          <button
            className="inline-action"
            type="button"
            onClick={() => {
              setTileError(false);
              setTileAttempt((value) => value + 1);
            }}
          >
            Retry street map
          </button>
        </p>
      )}
      {boundaryError && (
        <p role="status" className="map-message">
          Pilot boundary unavailable. No coverage boundary is shown.
        </p>
      )}
      <div className="map-provenance">
        <p>
          {valid
            ? location.scope
            : "No verified coordinate for this address. No property pin is shown."}{" "}
          The dashed line marks the council pilot area, not complete flood or
          service coverage.
        </p>
        <p>
          {locality
            ? "Coloured points show only the loaded council bus-stop and park records. Their locations do not establish service quality, routes or access."
            : "Load nearby records below to explore council bus-stop and park points."}{" "}
          No flood or planning overlay is assessed here. Street-map labels are
          contextual, not verified report findings.
        </p>
        {valid && (
          <p>
            <a href={location.source_url}>{location.source_name}</a> ·{" "}
            {location.licence} · Source period{" "}
            {displayDate(location.observation_period)} · Retrieved{" "}
            {displayDate(location.retrieved_at)}.
          </p>
        )}
        {boundary && (
          <details>
            <summary>Boundary source & scope</summary>
            <p>{boundary.properties.attribution}</p>
            <p>
              Source period{" "}
              {displayDate(boundary.properties.observation_period)} · Retrieved{" "}
              {displayDate(boundary.properties.retrieved_at)} ·{" "}
              <a href={boundary.properties.source_url}>
                Council boundary source
              </a>
            </p>
          </details>
        )}
        <small>
          Street tiles load from OpenStreetMap when a matched location is
          available or you choose to load the map.
        </small>
      </div>
    </section>
  );
}
